import { useEffect } from "react";
import { ArrowLeft, ArrowRight } from "lucide-react";
import BrandMark from "./BrandMark";

const SITE_URL = process.env.REACT_APP_SITE_URL;
const OG_IMAGE = `${SITE_URL}/assets/founder/airobotcamstudio-logo.png`;

export default function ArticlePage({ article }) {
  useEffect(() => {
    const path = window.location.pathname.replace(/\/+$/, "");
    const url = `${SITE_URL}${path}`;
    const prevTitle = document.title;
    const descEl = document.head.querySelector('meta[name="description"]');
    const prevDesc = descEl ? descEl.getAttribute("content") : null;

    document.title = article.metaTitle;
    if (descEl) descEl.setAttribute("content", article.metaDesc);

    const created = [];
    const modified = [];
    const upsertMeta = (attr, value, content) => {
      let el = document.head.querySelector(`meta[${attr}="${value}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, value);
        document.head.appendChild(el);
        created.push(el);
      } else {
        modified.push({ el, prev: el.getAttribute("content") });
      }
      el.setAttribute("content", content);
    };
    upsertMeta("property", "og:title", article.metaTitle);
    upsertMeta("property", "og:description", article.metaDesc);
    upsertMeta("property", "og:type", "article");
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", OG_IMAGE);
    upsertMeta("name", "twitter:card", "summary");
    upsertMeta("name", "twitter:title", article.metaTitle);
    upsertMeta("name", "twitter:description", article.metaDesc);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    const canonicalCreated = !canonical;
    const prevCanonical = canonicalCreated ? null : canonical.getAttribute("href");
    if (canonicalCreated) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    window.scrollTo(0, 0);
    return () => {
      document.title = prevTitle;
      if (descEl && prevDesc) descEl.setAttribute("content", prevDesc);
      modified.forEach(({ el, prev }) => el.setAttribute("content", prev));
      created.forEach((el) => el.remove());
      if (canonicalCreated) canonical.remove();
      else if (prevCanonical) canonical.setAttribute("href", prevCanonical);
    };
  }, [article]);

  const monthYear = article.date.split(" ").slice(1).join(" ");

  return (
    <div data-testid="article-page" className="min-h-screen bg-[#050505] text-white">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-black/70 backdrop-blur-2xl">
        <div className="mx-auto flex h-[64px] max-w-4xl items-center justify-between px-6">
          <a
            href="/"
            data-testid="article-logo"
            className="flex items-center gap-2.5 font-display text-sm font-semibold tracking-[0.25em] text-white"
          >
            <BrandMark className="h-7 w-7 rounded-md" testid="article-logo-mark" />
            AIROBOTCAM<span className="text-cyan-300">STUDIO</span>
          </a>
          <a
            href="/#updates"
            data-testid="article-back"
            className="flex shrink-0 items-center gap-2 text-[13px] font-medium text-cyan-200/70 transition-colors duration-300 hover:text-cyan-100"
          >
            <ArrowLeft size={15} />
            <span className="sm:hidden">Back</span>
            <span className="hidden sm:inline">Back to Updates</span>
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-16 lg:py-24">
        <div data-testid="article-brand" className="flex items-center gap-4">
          <BrandMark className="h-14 w-14 rounded-xl border border-white/10" testid="article-brand-logo" />
          <div>
            <p className="font-display text-sm font-semibold tracking-[0.25em] text-white">
              AIROBOTCAM<span className="text-cyan-300">STUDIO</span>
            </p>
            <p className="font-display mt-1 text-[11px] font-medium uppercase tracking-[0.35em] text-cyan-300/90">
              Journal
            </p>
          </div>
        </div>

        <h1
          data-testid="article-title"
          className="font-display mt-8 text-3xl font-light leading-tight tracking-tighter text-white sm:text-4xl lg:text-5xl"
        >
          {article.title}
        </h1>
        {article.subtitle && (
          <p data-testid="article-subtitle" className="mt-4 text-base leading-relaxed text-[#C5CBD5]">
            {article.subtitle}
          </p>
        )}
        <p data-testid="article-byline" className="mt-5 text-xs uppercase tracking-[0.2em] text-white/55">
          AIROBOTCAMSTUDIO LTD · Founder &amp; Vision Lead: Artur-Mihail Popescu · {monthYear} ·{" "}
          {article.readTime}
        </p>
        <p className="mt-8 border-l-2 border-cyan-300/40 pl-5 text-base leading-relaxed text-white/70">
          {article.intro}
        </p>

        {article.sections.map((s) => (
          <section key={s.heading} className="mt-12">
            <h2 className="font-display text-xl font-medium tracking-tight text-white">{s.heading}</h2>
            <div className="mt-4 space-y-4 text-sm leading-relaxed text-[#C5CBD5]">
              {s.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </section>
        ))}

        <div data-testid="article-related" className="mt-14 border-t border-white/10 pt-10">
          <p className="font-display text-[11px] font-semibold uppercase tracking-[0.3em] text-white/55">
            Related reading
          </p>
          <div className="mt-5 flex flex-col gap-3">
            {article.related.map(([label, href]) => (
              <a
                key={href}
                href={href}
                className="group inline-flex w-fit items-center gap-2 text-sm font-medium text-cyan-200/70 transition-colors duration-300 hover:text-cyan-100"
              >
                {label}
                <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5" />
              </a>
            ))}
          </div>
        </div>

        <div
          data-testid="article-endcard"
          className="mt-16 rounded-2xl border border-cyan-300/20 bg-cyan-300/[0.04] p-8 text-center sm:p-10"
        >
          <p className="font-display text-[11px] font-semibold uppercase tracking-[0.3em] text-cyan-300/90">
            Building the future of AI entertainment
          </p>
          <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row">
            <a
              href="/#technology"
              data-testid="article-cta-technology"
              className="group inline-flex items-center justify-center gap-2 rounded-md bg-cyan-300 px-7 py-3.5 text-sm font-bold text-black transition-transform duration-300 hover:-translate-y-0.5 hover:bg-cyan-200 active:scale-[0.98]"
            >
              Explore the Technology
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="/#contact"
              data-testid="article-cta-journey"
              className="group inline-flex items-center justify-center gap-2 rounded-md border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:border-white/60 active:scale-[0.98]"
            >
              Join the Journey
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>
          <a
            href="/#updates"
            data-testid="article-back-bottom"
            className="mt-7 inline-flex items-center gap-2 text-xs font-medium text-cyan-200/60 transition-colors duration-300 hover:text-cyan-100"
          >
            <ArrowLeft size={14} /> Back to Company Updates
          </a>
        </div>
      </main>

      <footer className="border-t border-white/10">
        <div className="mx-auto flex max-w-4xl flex-wrap items-center justify-between gap-4 px-6 py-8">
          <p className="text-[11px] uppercase tracking-[0.2em] text-white/55">
            AIROBOTCAMSTUDIO LTD · UK Company No. 17410621
          </p>
          <a href="/#updates" className="text-xs text-cyan-200/60 transition-colors hover:text-cyan-100">
            Back to Updates
          </a>
        </div>
      </footer>
    </div>
  );
}
